import { call, put, select } from 'redux-saga/effects';
import _ from 'underscore';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { Set } from '~commonInfra/utils/Set';
import { SessionSiteSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { DataSources } from '~src/datasource/resources/Datasources';
import { CreateDatasourceExtractTaskActionService } from '~src/extractTask/actions/CreateDatasourceExtractTaskAction';
import { CreateExtractTaskActionService, CreateExtractTasksMode } from '~src/extractTask/actions/CreateExtractTaskAction';
import { CreateMixedContentExtractTaskActionService } from '~src/extractTask/actions/CreateMixedContentExtractTaskAction';
import { CreateWorkbookExtractTaskActionService } from '~src/extractTask/actions/CreateWorkbookExtractTaskAction';
import { incrementRouterSpaceReactKey } from '~src/spacesPlatform/actions/IncrementRouterSpaceReactKeyAction';
import { requestSpaceContents } from '~src/spacesPlatform/actions/RequestSpaceContentsActions';
import { getDatasourceInfos, getWorkbookInfos } from '~src/spacesPlatform/sagas/SpacesSagaHelpers';
import { selectSpaceInfo } from '~src/spacesPlatform/selectors/SpaceInfoSelectors';
import { SpaceInfo } from '~src/spacesPlatform/state/SpaceInfo';

import { requestFailed } from '../../../bootstrap/redux/actions/RequestFailedAction';
import { getAppStore } from '../../../bootstrap/redux/store/AppStore';
import { Messages } from '../../../codegen/Localize';
import { IDataSourceBase, IDataSourceInfo } from '../../../datasource/models/IDataSourceInfo';
import { SetDatasourceRefreshModeRequest } from '../../../datasource/ServerApi/requests/SetDatasourceRefreshModeRequest';
import {SetDatasourceUsingRemoteQueryAgentRequest} from '../../../datasource/ServerApi/requests/SetDatasourceUsingRemoteQueryAgentRequest';
import { RefreshSelfServiceScheduleDialog } from '../../../extractTask/components/extractRefreshDialog/RefreshSelfServiceScheduleDialog';
import { IResult } from '../../../httpClient/serverApi/types/IResponse';
import { WebHttpClientWithErrorToastInstance } from '../../../httpClient/WebHttpClientWithErrorToast';
import { IContentInfo } from '../../../spacesPlatform/models/IContentInfo';
import { IWorkbookBase, IWorkbookInfo } from '../../../workbook/models/IWorkbookInfo';
import { CreateExtractRefreshAction } from '../../actions/extractRefresh/CreateExtractRefreshAction';


export function openWorkbookRefreshExtractDialog(
  workbooks: Set<IWorkbookBase>,
  mode: CreateExtractTasksMode
): Promise<IResult> {
  return CreateWorkbookExtractTaskActionService.forWorkbooks(workbooks, mode);
}

export function openDatasourceRefreshExtractDialog(
  datasources: Set<IDataSourceBase>,
  mode: CreateExtractTasksMode
): Promise<IResult> {
  return CreateDatasourceExtractTaskActionService.forDatasources(datasources, mode);
}

export function openContentRefreshExtractDialog(
  content: Set<IContentInfo>,
  mode: CreateExtractTasksMode
): Promise<IResult> {
  return CreateMixedContentExtractTaskActionService.forContent(content, mode);
}

export function setExtractRefreshMode(
  refreshMode: string,
  action: CreateExtractRefreshAction
){
  const datasources = action.selectedContent.filter(c => c.contentType === 'datasource');
  const dsIds = datasources.map(content => (content.id));

  for(const dsId of dsIds) {
    const changeModeRequest = new SetDatasourceRefreshModeRequest({
      id: dsId,
      refreshMode: refreshMode,
    });
    WebHttpClientWithErrorToastInstance.sendRequest(changeModeRequest).then(result => {
      getAppStore().dispatch(incrementRouterSpaceReactKey());
    }).catch(err => {
      CreateExtractRefreshAction.notifyResult();
    });
  }
}

export async function getExtractRefreshSchedulingAllowedForDatasources(datasourceInfos: IDataSourceInfo[]): Promise<boolean> {
  if (datasourceInfos.length > 1) {
    return true;
  }

  const result = await DataSources.getExtractRefreshSchedulingForDatasourceRequest(datasourceInfos[0].id);
  return result.errors ? true : result.allowed;
}

export function* createSelfServiceScheduleExtractAction(action: CreateExtractRefreshAction) {
  const contentDictionaryByType = _.groupBy(action.selectedContent, c => c.contentType);
  const workbookInfos = contentDictionaryByType['workbook'] && (yield call(getWorkbookInfos, contentDictionaryByType['workbook']));
  const datasourceInfos = contentDictionaryByType['datasource'] && (yield call(getDatasourceInfos, contentDictionaryByType['datasource']));
  let targetResources: (IDataSourceInfo | IWorkbookInfo)[] = [];
  if (workbookInfos) {
    targetResources = targetResources.concat(workbookInfos);
  }
  if (datasourceInfos) {
    targetResources = targetResources.concat(datasourceInfos);
  }
  let params = yield call(CreateExtractTaskActionService.getRefreshSSSDialogParams, targetResources);
  let result = yield call(RefreshSelfServiceScheduleDialog.open, params);
  return Promise.resolve(result);
}

export function* handleCreateExtractRefreshAction(action: CreateExtractRefreshAction) {
  const isExtractSSSEnabledForServer = FeatureFlags.selfServiceSchedulesForRefreshEnabled;
  const isExtractSSSEnabledForSite = SessionSiteSelectors.isSelfServiceSchedulesForRefreshEnabled(getAppStore().getState());
  const isCreateBridgeExtractRefresh = (action.mode === CreateExtractTasksMode.AllowScheduleOnlyOnClient);
  if (isExtractSSSEnabledForServer && isExtractSSSEnabledForSite && !isCreateBridgeExtractRefresh) {
    yield call(createSelfServiceScheduleExtractAction, action);
    yield call(setExtractRefreshMode, 'Server', action);
    if(isRefreshUsingRemoteAgent(action)){
      yield call(setUsingRemoteAgent, action);
    }
    yield put(requestSpaceContents());
  } else {
    const localizedErrorMessage = Messages.unexpectedError();
    try {
      const contentDictionaryByType = _.groupBy(action.selectedContent, c => c.contentType);
      const typeArray = Object.keys(contentDictionaryByType);

      if (typeArray.length === 1) {
        const singleContentType = typeArray[0];
        switch (singleContentType) {
          case 'workbook':
            const workbookInfos = yield call(getWorkbookInfos, contentDictionaryByType[singleContentType]);
            const workbookSet = Set.fromIdItemArray(workbookInfos);
            yield call(openWorkbookRefreshExtractDialog, workbookSet, action.mode);
            break;
          case 'datasource':
            const datasourceInfos = yield call(getDatasourceInfos, contentDictionaryByType[singleContentType]);
            const datasourceSet = Set.fromIdItemArray(datasourceInfos);
            let mode = action.mode;
            const isExtractRefreshSchedulingAllowedForDatasources: boolean = yield call(getExtractRefreshSchedulingAllowedForDatasources, datasourceInfos);
            if (!isExtractRefreshSchedulingAllowedForDatasources) {
              mode = CreateExtractTasksMode.DisableScheduleOnServerAndClient;
            }
            yield call(openDatasourceRefreshExtractDialog, datasourceSet, mode);
            break;
          default:
            yield put(requestFailed(`${singleContentType} does not support extract refresh.`));
            break;
        }
      } else if (typeArray.length > 1) {
        const workbookInfos = yield call(getWorkbookInfos, contentDictionaryByType['workbook']);
        const datasourceInfos = yield call(getDatasourceInfos, contentDictionaryByType['datasource']);
        const contentIds = Set.fromIdItemArray(workbookInfos.concat(datasourceInfos));
        yield call(openContentRefreshExtractDialog, contentIds, action.mode);
      }

      const spaceInfo: SpaceInfo = yield select(selectSpaceInfo);
      if (spaceInfo.subspaceName === 'extractTasks' || spaceInfo.subspaceName === 'extractRefreshes') {
        yield put(requestSpaceContents());
      }
    } catch (e) {
      yield put(requestFailed(e, localizedErrorMessage));
    }
  }
}

function isRefreshUsingRemoteAgent(action: CreateExtractRefreshAction) {
  return action.mode === CreateExtractTasksMode.AllowRefreshNowAndOnServerAndClient ||
    action.mode === CreateExtractTasksMode.AllowScheduleOnServerAndClient;
}

function setUsingRemoteAgent(action: CreateExtractRefreshAction) {
  const datasources = action.selectedContent.filter(c => c.contentType === 'datasource');
  const dsIds = datasources.map(content => (content.id));

  for (const dsId of dsIds) {
    const setDatasourceUsingRemoteQueryAgentRequest = new SetDatasourceUsingRemoteQueryAgentRequest({
      id: dsId,
    });
    WebHttpClientWithErrorToastInstance.sendRequest(setDatasourceUsingRemoteQueryAgentRequest)
    .catch(err => {
      CreateExtractRefreshAction.notifyResult();
    });
  }
}
